// Order History - Full filterable order log
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export const OrderHistory = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [dateRange, setDateRange] = useState('all'); // all, 1M, 3M, 6M, 1Y
  const [fundFilter, setFundFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all'); // all, buy, sell
  const [statusFilter, setStatusFilter] = useState('all'); // all, filled, submitted, rejected

  useEffect(() => {
    loadOrders();
    // Near-real-time polling — refresh every 60s so completed transactions
    // (IFTs, withdrawals, Tier-1 trades) surface within 1–2 minutes of execution.
    const id = setInterval(loadOrders, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, dateRange, fundFilter, orderTypeFilter, statusFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/investments/orders');
      setOrders(response.data.orders || []);
      setFilteredOrders(response.data.orders || []);

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load order history');
      console.error('Order history load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      const ranges = {
        '1M': 30,
        '3M': 90,
        '6M': 180,
        '1Y': 365
      };
      const days = ranges[dateRange];
      const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

      filtered = filtered.filter(order => 
        new Date(order.created_at) >= cutoffDate
      );
    }

    // Fund filter
    if (fundFilter !== 'all') {
      filtered = filtered.filter(order => order.symbol === fundFilter);
    }

    // Order type filter (works with both legacy `order_type` and unified `type`)
    if (orderTypeFilter !== 'all') {
      filtered = filtered.filter(order =>
        (order.order_type || order.type || '').toLowerCase() === orderTypeFilter.toLowerCase()
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    setFilteredOrders(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'filled': 'bg-[#E6F7E6] text-[#28A745]',
      'submitted': 'bg-[#FFF9E6] text-[#B8860B]',
      'rejected': 'bg-[#FFF5F5] text-[#D73A49]',
      'pending': 'bg-[#F6F8FA] text-[#586069]'
    };
    return colors[status] || colors.pending;
  };

  const getOrderTypeColor = (orderType) => {
    return orderType === 'buy' 
      ? 'text-[#28A745]' 
      : 'text-[#D73A49]';
  };

  // Get unique funds for filter
  const uniqueFunds = ['all', ...new Set(orders.map(o => o.symbol))];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <p className="text-[#586069]">Loading order history...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E1E4E8]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold text-[#24292E]">Order History</h1>
          <p className="text-sm text-[#586069] mt-1">
            View all your investment transactions
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-[#FFF5F5] border border-[#FEB2B2] rounded px-4 py-3 text-[#C53030] mb-6">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#24292E] mb-4">Filters</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-[#24292E] mb-2">
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-3 py-2 border border-[#D1D5DB] rounded text-sm focus:outline-none focus:border-[#0366D6] focus:ring-1 focus:ring-[#0366D6]"
              >
                <option value="all">All Time</option>
                <option value="1M">Last Month</option>
                <option value="3M">Last 3 Months</option>
                <option value="6M">Last 6 Months</option>
                <option value="1Y">Last Year</option>
              </select>
            </div>

            {/* Fund */}
            <div>
              <label className="block text-sm font-medium text-[#24292E] mb-2">
                Fund
              </label>
              <select
                value={fundFilter}
                onChange={(e) => setFundFilter(e.target.value)}
                className="w-full px-3 py-2 border border-[#D1D5DB] rounded text-sm focus:outline-none focus:border-[#0366D6] focus:ring-1 focus:ring-[#0366D6]"
              >
                {uniqueFunds.map(fund => (
                  <option key={fund} value={fund}>
                    {fund === 'all' ? 'All Funds' : fund}
                  </option>
                ))}
              </select>
            </div>

            {/* Order Type */}
            <div>
              <label className="block text-sm font-medium text-[#24292E] mb-2">
                Order Type
              </label>
              <select
                value={orderTypeFilter}
                onChange={(e) => setOrderTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-[#D1D5DB] rounded text-sm focus:outline-none focus:border-[#0366D6] focus:ring-1 focus:ring-[#0366D6]"
              >
                <option value="all">All Types</option>
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-[#24292E] mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-[#D1D5DB] rounded text-sm focus:outline-none focus:border-[#0366D6] focus:ring-1 focus:ring-[#0366D6]"
              >
                <option value="all">All Statuses</option>
                <option value="filled">Filled</option>
                <option value="submitted">Submitted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-[#586069]">
            Showing {filteredOrders.length} of {orders.length} orders
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] overflow-hidden">
          {filteredOrders.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-[#D1D5DB] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-[#586069]">No orders found matching your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F6F8FA] border-b border-[#E1E4E8]">
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Fund</th>
                    <th className="text-center py-3 px-4 font-medium text-[#24292E]">Type</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Shares</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Price</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Total</th>
                    <th className="text-center py-3 px-4 font-medium text-[#24292E]">Status</th>
                    <th className="text-center py-3 px-4 font-medium text-[#24292E]">Compliance</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, index) => {
                    const isInvestment = (order.source_collection || 'investment_orders') === 'investment_orders';
                    const typeLabel = (order.type || order.order_type || '').toString().replace(/_/g, ' ');
                    return (
                    <tr key={order.id || index} className="border-b border-[#E1E4E8] hover:bg-[#F6F8FA]" data-testid={`order-row-${order.id || index}`}>
                      <td className="py-3 px-4 text-[#24292E]">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-mono font-semibold text-[#0366D6]">
                          {order.symbol}
                        </div>
                        {order.description && (
                          <div className="text-[10px] text-[#586069] truncate max-w-[220px]" title={order.description}>
                            {order.description}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-semibold uppercase text-xs ${getOrderTypeColor(order.order_type || order.type)}`}>
                          {typeLabel || '—'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-[#24292E]">
                        {typeof order.shares === 'number' ? order.shares.toFixed(4) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right text-[#24292E]">
                        {order.filled_price ? formatCurrency(order.filled_price) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-[#24292E]">
                        {typeof order.amount === 'number' ? formatCurrency(order.amount) : '—'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {isInvestment ? (
                          order.compliance_approved ? (
                            <div className="flex items-center justify-center gap-1">
                              <svg className="w-4 h-4 text-[#28A745]" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              {order.compliance_warnings && order.compliance_warnings.length > 0 && (
                                <span className="text-xs text-[#B8860B]" title={order.compliance_warnings.join(', ')}>
                                  !
                                </span>
                              )}
                            </div>
                          ) : (
                            <svg className="w-4 h-4 text-[#D73A49] mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          )
                        ) : (
                          <span className="text-[10px] text-[#586069] font-mono uppercase tracking-wider">{(order.source_collection || '').replace(/_/g, ' ')}</span>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Rebalancing Orders Notice */}
        {filteredOrders.some(o => o.rebalancing_order) && (
          <div className="mt-4 bg-[#F6F8FA] border border-[#E1E4E8] rounded px-4 py-3">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-[#0366D6] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-[#586069]">
                Some orders were executed as part of portfolio rebalancing
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
